/**
 * @file        src/main.jsx
 * @description React entrypoint. Mounts router + react-query provider.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-10-07 Claude — Comment only: prod now served at the root of
 *                         https://beyonddestination.in/, so basename is '/'.
 *   - 2026-10-01 Claude — installImageFallback(): one retry, then a placeholder,
 *                         for any image that fails to load (hotel supplier CDNs).
 *   - 2026-09-21 Claude — basename derived from import.meta.env.BASE_URL, not
 *                         hardcoded, so stage (served at root) routes again.
 *   - 2026-09-16 Claude — Added BrowserRouter basename="/beyond"
 *                         for production deployment under /beyond/.
 *   - 2026-07-20 Claude — Register the 401 handler.
 *   - 2026-06-22 Claude — Initial.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.jsx';
import { onUnauthorized } from './api/client.js';
import { installImageFallback } from './utils/imageFallback.js';
import './styles/global.css';

// Follows Vite's `base` (VITE_BASE_PATH): '/' on prod (beyonddestination.in),
// stage and dev. Trailing slash stripped — React Router won't match a sub-path
// base such as '/beyond' against '/beyond/'.
const ROUTER_BASENAME = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

// Broken supplier photos (hotel CDNs) → one retry, then a placeholder.
installImageFallback();

// Fires only after the interceptor's silent refresh has already failed.
onUnauthorized(() => {
  window.dispatchEvent(new CustomEvent('nh:open-auth'));
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={ROUTER_BASENAME}>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);